import { getRkiConfigurationByOrganizationIdV1 } from '@ingenico/api-sdk-device-management-public';

import type {
  SearchOsVersionsV2MutationRequestApi,
  SearchRequestApi,
} from '@ingenico/api-sdk-device-management-private-v2';
import {
  downloadAssetByIdV2,
  getApplicationVersionTemplatesByApplicationVersionIdV2,
  getConfigurationByIdBffV2,
  getConfigurationModuleByIdBffV2,
  getSystemPackageVersionTemplatesBySystemPackageVersionIdV2,
  getTemplateByIdV2,
  searchApplicationsV2,
  searchConfigurationModulesBffV2,
  searchOsV2,
  searchOsVersionsV2,
  searchSystemPackagesV2,
  searchSystemPackageVersionsV2,
} from '@ingenico/api-sdk-device-management-private-v2';
import type {
  ApiFnListFormBuilder,
  CategoryEnum,
  CGInputs,
  CGNavigationFnList,
  ConfigurationResourceBodyFront,
  HeaderSelectOption,
  ReactConfigurationGeneratorApi,
  SearchRequestApiFront,
  UpdateConfigurationFront,
} from '@ingenico/react-configuration-generator';
import {
  defaultConfigurationGeneratorCategory,
  TargetEntityTypeEnum,
  useConfigurationGeneratorBuilderContext,
} from '@ingenico/react-configuration-generator';
import { useReactFormGeneratorContext } from '@ingenico/react-form-generator';
import {
  useCurrentUser,
  useCurrentUserDateTimeFormat,
  useGetCoreClient,
  useSectionContainer,
} from '@ingenico/react-section-utils';

// import { Banner } from '@ingenico/shared/banner';
import { logError } from '@ingenico/util-common';
import type { ForwardedRef } from 'react';
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { useStore } from 'jotai';
import { useParams } from 'react-router';
import { defer, EMPTY, map, of, tap } from 'rxjs';
import { selectedLogicalDeviceIdAtom } from '../../atoms/selectedLogicalDevice.atom';
import { ConfigurationFormGeneratorService } from '../../domain/business/services/configurationFormGenerator.service';
import { useDeviceManagementCapability } from '../../hooks/useDeviceManagementCapability.hook';
import { useDeviceManagementSectionRoutePrefixHook } from '../../hooks/useDeviceManagementSectionRoutePrefix.hook';
import { Banner } from '../../shared-inlined/banner';
import type {
  ConfigurationFormBuilderWrapperOutputV2,
  IUseConfigurationFormBuilderWrapperProps,
  ReactConfigurationGeneratorBuilderApi,
} from './configurationFormBuilderWrapperV2.model';
import {
  getConfigurationFormBuilderTranslations,
  getFormGeneratorTranslations,
  getHeaderSelectOptions,
  getSelectedDeviceParameterSets,
} from './configurationFormBuilderWrapperV2.utils';

export function useConfigurationFormBuilderWrapperV2(
  { inputs, t, i18n, initialConfiguration }: IUseConfigurationFormBuilderWrapperProps,
  ref: ForwardedRef<ReactConfigurationGeneratorApi>,
): ConfigurationFormBuilderWrapperOutputV2 {
  const reactConfigurationGeneratorRef = useRef<ReactConfigurationGeneratorBuilderApi>(null);
  const shouldUseInitialConfigurationRef = useRef<boolean>(!!initialConfiguration);

  useImperativeHandle(
    ref,
    () => ({
      forceCleanChanges: () => reactConfigurationGeneratorRef?.current?.forceCleanChanges(),
      isEditMode: () => reactConfigurationGeneratorRef?.current?.isEditMode() ?? false,
      setEditEnabled: (value: boolean) => {
        reactConfigurationGeneratorRef?.current?.setEditEnabled(value);
      },
      handleSelectCategory: (category: CategoryEnum) => {
        return reactConfigurationGeneratorRef.current?.handleSelectCategory(category);
      },
      listenToCategorySelectionChange: () =>
        reactConfigurationGeneratorRef?.current?.listenToCategorySelectionChange() ??
        of(defaultConfigurationGeneratorCategory.category),
      handleExpandAppAccordion: (
        expandedElementId: string,
        expanded: boolean,
        getHTMLElement?: (element: HTMLDivElement) => void,
      ) =>
        reactConfigurationGeneratorRef?.current?.handleExpandAppAccordion(expandedElementId, expanded, getHTMLElement),
      handleExpandSpAccordion: (
        expandedElementId: string,
        expanded: boolean,
        getHTMLElement?: (element: HTMLDivElement) => void,
      ) =>
        reactConfigurationGeneratorRef?.current?.handleExpandSpAccordion(expandedElementId, expanded, getHTMLElement),
      hasLoadedRootConfig$: reactConfigurationGeneratorRef?.current?.hasLoadedRootConfig$ ?? of(false),
      refetch: () => {
        reactConfigurationGeneratorRef?.current?.refetch();
      },
      setHeaderSelectValue: (value: string | undefined) =>
        reactConfigurationGeneratorRef?.current?.setHeaderSelectValue(value),
      setHeaderSelectOptions: (options: readonly HeaderSelectOption[] | undefined) =>
        reactConfigurationGeneratorRef?.current?.setHeaderSelectOptions(options),
      headerSelectChange$: defer(() => reactConfigurationGeneratorRef.current?.headerSelectChange$ ?? EMPTY),
    }),
    [],
  );

  const container = useSectionContainer();
  const coreClient = useGetCoreClient();

  const configurationFormGeneratorService = useMemo(
    () => container.get(ConfigurationFormGeneratorService),
    [container],
  );
  const deviceManagementRoutePrefix = useDeviceManagementSectionRoutePrefixHook();
  const editCapability = useDeviceManagementCapability('configProfile.edit');
  const showRkiCapability = useDeviceManagementCapability('rki.showInGlobalNavigation');
  const isConfigProfilePutEnabled = useDeviceManagementCapability('configProfile.enableConfigProfilePut');
  const keysetSegregationCapability = useDeviceManagementCapability('configProfile.keysetSegregation');
  const user = useCurrentUser();

  const isEditEnabled = useMemo<boolean>(
    () => editCapability && !inputs.isReadOnly,
    [editCapability, inputs.isReadOnly],
  );

  const onCancel = useMemo(
    () => (resetState: () => void) => {
      resetState();
    },
    [],
  );

  const refetchConfigurationById = useCallback(
    (saveFn?: (configurationId: string) => void) => (configurationId: string) => {
      void Promise.all([
        coreClient.deleteAllCacheOfEndpoint(getConfigurationByIdBffV2.name),
        coreClient.deleteAllCacheOfEndpoint(searchOsV2.name),
        coreClient.deleteAllCacheOfEndpoint(getRkiConfigurationByOrganizationIdV1.name),
        coreClient.deleteAllCacheOfEndpoint(searchOsVersionsV2.name),
        coreClient.deleteAllCacheOfEndpoint(searchApplicationsV2.name),
        coreClient.deleteAllCacheOfEndpoint(getApplicationVersionTemplatesByApplicationVersionIdV2.name),
        coreClient.deleteAllCacheOfEndpoint(getSystemPackageVersionTemplatesBySystemPackageVersionIdV2.name),
        coreClient.deleteAllCacheOfEndpoint(searchSystemPackagesV2.name),
        coreClient.deleteAllCacheOfEndpoint(downloadAssetByIdV2.name),
        coreClient.deleteAllCacheOfEndpoint(searchConfigurationModulesBffV2.name),
        coreClient.deleteAllCacheOfEndpoint(searchSystemPackageVersionsV2.name),
        coreClient.deleteAllCacheOfEndpoint(getTemplateByIdV2.name),
        coreClient.deleteAllCacheOfEndpoint(getConfigurationModuleByIdBffV2.name),
      ])
        .then(() => (saveFn ? saveFn(configurationId) : reactConfigurationGeneratorRef.current?.refetch()))
        .catch(logError);
    },
    [coreClient],
  );

  const mappedInputs: CGInputs = {
    ...inputs,
    isEditEnabled,
    translations: getConfigurationFormBuilderTranslations(t, inputs.configurationInput.entityType),
    fgTranslations: getFormGeneratorTranslations(t, i18n),
    fgComponents: {
      dateTimeFormat: useCurrentUserDateTimeFormat(),
      banner: Banner,
    },
    capabilities: {
      rki: { showInGlobalNavigation: !!showRkiCapability, keysetSegregation: !!keysetSegregationCapability },
    },
    actions: {
      onCancel: inputs.actions?.onCancel ?? onCancel,
      onSave: refetchConfigurationById(inputs.actions?.onSave),
    },
  };

  const getAssetLogoById = useCallback(
    (assetId: string) => configurationFormGeneratorService.downloadSystemPackageOrApplicationLogoByIdV2(assetId),
    [configurationFormGeneratorService],
  );

  const getOsList = useCallback(
    (searchParams: SearchRequestApiFront) =>
      configurationFormGeneratorService.getOsList(searchParams, { limit: 50, offset: 0 }),
    [configurationFormGeneratorService],
  );

  const getOsVersionsById = useCallback(
    (osId: string) => {
      const body: SearchOsVersionsV2MutationRequestApi = {
        filters: [{ name: 'osVersion.osId', value: osId, filterType: 'string', operator: 'equals' }],
        sort: { field: 'osVersion.versionNumber', order: 'ASC' },
      };
      return configurationFormGeneratorService.getOsVersionList(body, { disableCache: true });
    },
    [configurationFormGeneratorService],
  );

  const getRkiKeySetReference = useCallback(
    () => configurationFormGeneratorService.getRkiKeySetReference(),
    [configurationFormGeneratorService],
  );

  const getOrganizationKeySetsForProfile = useCallback(() => {
    return configurationFormGeneratorService.getOrganizationKeySetsForProfile(user.parent.id); //It works the best if user.parent.type is organization.
  }, [configurationFormGeneratorService, user]);

  const getTemplatesByAppsOrSystemPackageVersion = useCallback(
    (isApp: boolean, versionId: string) =>
      configurationFormGeneratorService.getTemplatesByAppsOrSystemPackageVersion(isApp, versionId),
    [configurationFormGeneratorService],
  );

  const searchApplicationsOrSystemPackage = useCallback(
    (isApp: boolean, searchParams: SearchRequestApiFront) => {
      return configurationFormGeneratorService.searchApplicationsOrSystemPackage(searchParams, isApp, {
        limit: 100,
        offset: 0,
      });
    },
    [configurationFormGeneratorService],
  );

  const searchConfigurationModules = useCallback(
    (searchParams: SearchRequestApiFront) => {
      return configurationFormGeneratorService.searchConfigurationModules(searchParams, {
        limit: 100,
        offset: 0,
      });
    },
    [configurationFormGeneratorService],
  );

  const saveConfiguration = useCallback(
    (configurationId: string, update: UpdateConfigurationFront) =>
      configurationFormGeneratorService.saveConfiguration(configurationId, update, isConfigProfilePutEnabled),
    [configurationFormGeneratorService, isConfigProfilePutEnabled],
  );

  const searchAppsOrSystemPackageVersions = useCallback(
    (isApp: boolean, id: string) => {
      const body: SearchRequestApi = {
        filters: [
          {
            name: isApp ? 'applicationVersion.applicationId' : 'systemPackageVersion.systemPackageId',
            value: id,
            filterType: 'string',
            operator: 'equals',
          },
          {
            filterType: 'array',
            name: isApp ? 'applicationVersion.configurationType' : 'systemPackageVersion.configurationType',
            operator: 'in',
            value: JSON.stringify(['WITHOUT_PARAMETERS']),
          },
        ],
        sort: {
          field: isApp ? 'applicationVersion.versionNumber' : 'systemPackageVersion.versionNumber',
          order: 'ASC',
        },
      };
      return configurationFormGeneratorService.searchAppsOrSystemPackageVersions(isApp, body);
    },
    [configurationFormGeneratorService],
  );

  const getConfiguration = useCallback(() => {
    if (shouldUseInitialConfigurationRef.current && initialConfiguration) {
      shouldUseInitialConfigurationRef.current = false;
      return of(initialConfiguration);
    }

    const body: ConfigurationResourceBodyFront =
      inputs.configurationInput.entityType === TargetEntityTypeEnum.LOGICAL_DEVICE
        ? {
            entity: TargetEntityTypeEnum.LOGICAL_DEVICE,
            deviceId: inputs.configurationInput.id,
          }
        : { entity: TargetEntityTypeEnum.CONFIG_PROFILE, configurationId: inputs.configurationInput.id };
    return configurationFormGeneratorService.getConfiguration(body, { disableCache: true });
  }, [
    configurationFormGeneratorService,
    initialConfiguration,
    inputs.configurationInput.entityType,
    inputs.configurationInput.id,
  ]);

  const { getFormBuilderConfig: getFormBuilderConfigFromContext } = useConfigurationGeneratorBuilderContext();
  const { logicalDeviceId } = useParams();
  const isFirstHeaderSelectLoadRef = useRef(true);
  const isDmMultiMerchantEnabled = useDeviceManagementCapability('deployments.isDmMultiMerchantEnabled');

  const store = useStore();
  // Starts with the logical device from the URL; follows the header select afterwards.
  const selectedLogicalDeviceIdRef = useRef<string | undefined>(logicalDeviceId);

  const getFormBuilderConfig = useCallback<typeof getFormBuilderConfigFromContext>(
    (
      templateId: string,
      isSnapshotId: boolean,
      configurationModuleId: string,
      deploymentUnitId: string,
      smartFieldPrefix: string,
      options?: {
        readonly isRefresh: boolean;
      },
    ) => {
      if (isFirstHeaderSelectLoadRef.current) {
        store.set(selectedLogicalDeviceIdAtom, logicalDeviceId);
      }
      return getFormBuilderConfigFromContext(
        templateId,
        isSnapshotId,
        configurationModuleId,
        deploymentUnitId,
        smartFieldPrefix,
        options,
      ).pipe(
        tap(({ configModule }) => {
          if (!isDmMultiMerchantEnabled) {
            return;
          }

          const options = getHeaderSelectOptions(
            configModule?.parameterSets,
            selectedLogicalDeviceIdRef.current,
            templateId,
          );

          const builderApi = reactConfigurationGeneratorRef.current;
          builderApi?.setHeaderSelectOptions(options);
          if (isFirstHeaderSelectLoadRef.current) {
            isFirstHeaderSelectLoadRef.current = false;
          }
          const initialOption = options.find(
            ({ value }) =>
              value === (isFirstHeaderSelectLoadRef.current ? logicalDeviceId : selectedLogicalDeviceIdRef.current),
          );
          if (initialOption) {
            builderApi?.setHeaderSelectValue(initialOption.value);
          }
        }),
        map(({ configModule, ...response }) => ({
          ...response,
          configModule:
            isDmMultiMerchantEnabled && configModule
              ? {
                  ...configModule,
                  parameterSets: getSelectedDeviceParameterSets(
                    configModule.parameterSets,
                    selectedLogicalDeviceIdRef.current,
                  ),
                }
              : configModule,
        })),
      );
    },
    [getFormBuilderConfigFromContext, isDmMultiMerchantEnabled, logicalDeviceId, store],
  );

  const getRouteToOsDetails = useCallback(
    (osId: string) => `${deviceManagementRoutePrefix}/os/${osId}`,
    [deviceManagementRoutePrefix],
  );
  const getRouteToAppDetails = useCallback(
    (appId: string) => `${deviceManagementRoutePrefix}/apps/${appId}`,
    [deviceManagementRoutePrefix],
  );
  const getRouteToSystemPackageDetails = useCallback(
    (systemPackageId: string) => `${deviceManagementRoutePrefix}/system-packages/${systemPackageId}`,
    [deviceManagementRoutePrefix],
  );
  const getRouteToOsList = useCallback(() => `${deviceManagementRoutePrefix}/os`, [deviceManagementRoutePrefix]);

  const navigationFnList: CGNavigationFnList = useMemo(
    () => ({
      getRouteToOsDetails,
      getRouteToOsList,
      getRouteToAppDetails,
      getRouteToSystemPackageDetails,
    }),
    [getRouteToOsDetails, getRouteToOsList, getRouteToAppDetails, getRouteToSystemPackageDetails],
  );

  const { isAbleToRenderTopHint } = useReactFormGeneratorContext();

  const checkShouldDisplayTopHint = useCallback(() => of(isAbleToRenderTopHint), [isAbleToRenderTopHint]);

  const apiFnList: ApiFnListFormBuilder = useMemo(
    () => ({
      getOsList,
      getOsVersionsById,
      getRkiKeySetReference,
      getOrganizationKeySetsForProfile,
      getAssetLogoById,
      getTemplatesByAppsOrSystemPackageVersion,
      saveConfiguration,
      searchApplicationsOrSystemPackage,
      searchConfigurationModules,
      searchAppsOrSystemPackageVersions,
      getConfiguration,
      getFormBuilderConfig,
      checkShouldDisplayTopHint,
    }),
    [
      getOsList,
      getOsVersionsById,
      getRkiKeySetReference,
      getOrganizationKeySetsForProfile,
      getAssetLogoById,
      getTemplatesByAppsOrSystemPackageVersion,
      saveConfiguration,
      searchApplicationsOrSystemPackage,
      searchConfigurationModules,
      searchAppsOrSystemPackageVersions,
      getConfiguration,
      getFormBuilderConfig,
      checkShouldDisplayTopHint,
    ],
  );

  useEffect(() => {
    // Captured here because the ref is already null when the cleanup runs.
    const builderApi = reactConfigurationGeneratorRef.current;
    const subscription = builderApi?.headerSelectChange$?.subscribe((value) => {
      selectedLogicalDeviceIdRef.current = value;
      store.set(selectedLogicalDeviceIdAtom, value);
      reactConfigurationGeneratorRef.current?.setHeaderSelectValue(value);
      reactConfigurationGeneratorRef.current?.refreshCurrentView();
    });

    builderApi?.onCategoryUnmount(() => {
      builderApi?.setHeaderSelectOptions?.([]);
      builderApi?.setHeaderSelectValue?.(undefined);
    });

    return () => subscription?.unsubscribe();
  }, [store]);

  return {
    apiFnList,
    navigationFnList,
    mappedInputs,
    reactConfigurationGeneratorRef,
  };
}
